<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { INBOX_TYPES, getInboxIconByType } from 'dashboard/helper/inbox';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const emit = defineEmits(['disconnect']);
const { t } = useI18n();
const [showMenu, toggleMenu] = useToggle();

const inboxName = computed(() => {
  const inbox = props.inbox;
  if (!inbox?.name) return '';

  const isTwilio = inbox.channel_type === INBOX_TYPES.TWILIO;
  const isWhatsApp = inbox.channel_type === INBOX_TYPES.WHATSAPP;
  if (isTwilio || isWhatsApp) {
    const identifier = inbox.messaging_service_sid || inbox.phone_number;
    return identifier ? `${inbox.name} (${identifier})` : inbox.name;
  }

  if (inbox.channel_type === INBOX_TYPES.EMAIL && inbox.email) {
    return `${inbox.name} (${inbox.email})`;
  }

  return inbox.name;
});

const icon = computed(() =>
  getInboxIconByType(
    props.inbox.channel_type,
    props.inbox.medium,
    'outline',
    props.inbox.voice_enabled
  )
);

const menuItems = computed(() => [
  {
    label: t('ARROWS_AI.INBOXES.DISCONNECT'),
    value: 'disconnect',
    action: 'disconnect',
    icon: 'i-lucide-trash',
  },
]);

const handleAction = ({ action }) => {
  toggleMenu(false);
  if (action === 'disconnect') emit('disconnect', props.inbox.id);
};
</script>

<template>
  <CardLayout>
    <div class="flex justify-between w-full gap-1">
      <span
        class="flex items-center gap-2 text-base text-n-slate-12 line-clamp-1"
      >
        <span :class="icon" />
        {{ inboxName }}
      </span>
      <div class="relative">
        <Button
          icon="i-lucide-ellipsis-vertical"
          color="slate"
          size="xs"
          @click="toggleMenu()"
        />
        <DropdownMenu
          v-if="showMenu"
          :menu-items="menuItems"
          class="mt-1 ltr:right-0 top-full"
          @action="handleAction"
        />
      </div>
    </div>
  </CardLayout>
</template>
